import { useEffect } from 'react';

type JsonLd = Record<string, unknown> | Array<Record<string, unknown>> | null;

interface SEOProps {
  title: string;
  description?: string;
  canonical?: string;
  openGraph?: {
    type?: string;
    image?: string;
  };
  jsonLd?: JsonLd;
}

export function SEO({ title, description, canonical, openGraph, jsonLd }: SEOProps) {
  useEffect(() => {
    document.title = title;

    const ensureMeta = (name: string, attr: 'name' | 'property' = 'name') => {
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      return el as HTMLMetaElement;
    };

    if (description) {
      ensureMeta('description').setAttribute('content', description);
      ensureMeta('og:description', 'property').setAttribute('content', description);
    }

    ensureMeta('og:title', 'property').setAttribute('content', title);
    ensureMeta('og:type', 'property').setAttribute('content', openGraph?.type || 'website');
    if (openGraph?.image) {
      ensureMeta('og:image', 'property').setAttribute('content', openGraph.image);
      ensureMeta('twitter:card', 'name').setAttribute('content', 'summary_large_image');
      ensureMeta('twitter:image', 'name').setAttribute('content', openGraph.image);
    }

    if (canonical) {
      let link = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      link.href = canonical;
    }

    const ldScripts: HTMLScriptElement[] = [];
    const jsonLdEntries = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

    jsonLdEntries.forEach((entry) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(entry);
      document.head.appendChild(script);
      ldScripts.push(script);
    });

    return () => {
      ldScripts.forEach((script) => {
        if (document.head.contains(script)) {
          document.head.removeChild(script);
        }
      });
    };
  }, [title, description, canonical, openGraph?.type, openGraph?.image, jsonLd]);

  return null;
}

